import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Separator } from '../ui/separator'
import { companyAPI } from '../../services/api'
import { toast } from '../../hooks/use-toast'
import {
  Loader2,
  Plus,
  X,
  Twitter,
  Linkedin,
  Facebook,
  AlertCircle,
  Building2
} from 'lucide-react'
import { validateLinkedInURL, validateTwitterURL, validateFacebookURL, validateURL, normalizeURL } from '../../utils/validation'

export default function BrandPromotionModal({ open, onClose }) {
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(false)
  const [existingData, setExistingData] = useState(null)
  const [errors, setErrors] = useState({})

  // Form data state
  const [formData, setFormData] = useState({
    twitter: {
      organizationProfile: '',
      tagProfiles: []
    },
    linkedin: {
      organizationProfile: '',
      tagProfiles: []
    },
    facebook: {
      organizationProfile: '',
      tagProfiles: []
    }
  })

  // Input states for adding new profiles
  const [newTwitterProfile, setNewTwitterProfile] = useState('')
  const [newLinkedInProfile, setNewLinkedInProfile] = useState('')
  const [newFacebookProfile, setNewFacebookProfile] = useState('')

  // Fetch existing data when modal opens
  useEffect(() => {
    if (open) {
      fetchExistingData()
    } else {
      // Reset state when modal closes
      resetForm()
    }
  }, [open])

  const fetchExistingData = async () => {
    setFetchingData(true)
    try {
      const response = await companyAPI.getBrandPromotion()
      if (response.data.success && response.data.data) {
        setExistingData(response.data.data)

        // Pre-populate form with existing data
        setFormData({
          twitter: response.data.data.twitter || { organizationProfile: '', tagProfiles: [] },
          linkedin: response.data.data.linkedin || { organizationProfile: '', tagProfiles: [] },
          facebook: response.data.data.facebook || { organizationProfile: '', tagProfiles: [] }
        })
      }
    } catch (error) {
      console.log('No existing brand promotion data found')
    } finally {
      setFetchingData(false)
    }
  }

  const resetForm = () => {
    setFormData({
      twitter: { organizationProfile: '', tagProfiles: [] },
      linkedin: { organizationProfile: '', tagProfiles: [] },
      facebook: { organizationProfile: '', tagProfiles: [] }
    })
    setNewTwitterProfile('')
    setNewLinkedInProfile('')
    setNewFacebookProfile('')
    setErrors({})
    setExistingData(null)
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate LinkedIn company profile (required)
    const linkedInValidation = validateLinkedInURL(formData.linkedin.organizationProfile)
    if (!linkedInValidation.isValid) {
      newErrors.linkedin = linkedInValidation.error
    }

    // Validate Twitter organization profile if provided
    if (formData.twitter.organizationProfile && formData.twitter.organizationProfile.trim()) {
      const twitterValidation = validateTwitterURL(formData.twitter.organizationProfile)
      if (!twitterValidation.isValid) {
        newErrors.twitter = twitterValidation.error
      }
    }

    // Validate Facebook organization profile if provided
    if (formData.facebook.organizationProfile && formData.facebook.organizationProfile.trim()) {
      const facebookValidation = validateFacebookURL(formData.facebook.organizationProfile)
      if (!facebookValidation.isValid) {
        newErrors.facebook = facebookValidation.error
      }
    }

    // Validate profile counts (0-5 each)
    if (formData.twitter.tagProfiles.length > 5) {
      newErrors.twitterProfiles = 'Maximum 5 X (Twitter) profile links allowed'
    }

    if (formData.linkedin.tagProfiles.length > 5) {
      newErrors.linkedinProfiles = 'Maximum 5 LinkedIn profile links allowed'
    }

    if (formData.facebook.tagProfiles.length > 5) {
      newErrors.facebookProfiles = 'Maximum 5 Facebook profile links allowed'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAddTwitterProfile = () => {
    if (!newTwitterProfile.trim()) return

    if (formData.twitter.tagProfiles.length >= 5) {
      toast.error('Maximum 5 X (Twitter) profile links allowed')
      return
    }

    // Normalize URL (add https:// if missing)
    const normalizedURL = normalizeURL(newTwitterProfile.trim())

    // Validate Twitter URL before adding (only URLs, not handles)
    const validation = validateTwitterURL(normalizedURL)
    if (!validation.isValid) {
      toast.error(validation.error)
      return
    }

    setFormData(prev => ({
      ...prev,
      twitter: {
        ...prev.twitter,
        tagProfiles: [...prev.twitter.tagProfiles, normalizedURL]
      }
    }))
    setNewTwitterProfile('')
    setErrors({ ...errors, twitterProfiles: '' })
  }

  const handleRemoveTwitterProfile = (index) => {
    setFormData(prev => ({
      ...prev,
      twitter: {
        ...prev.twitter,
        tagProfiles: prev.twitter.tagProfiles.filter((_, i) => i !== index)
      }
    }))
  }

  const handleAddLinkedInProfile = () => {
    if (!newLinkedInProfile.trim()) return

    if (formData.linkedin.tagProfiles.length >= 5) {
      toast.error('Maximum 5 LinkedIn profile links allowed')
      return
    }

    // Normalize URL (add https:// if missing)
    const normalizedURL = normalizeURL(newLinkedInProfile.trim())

    // Validate LinkedIn URL before adding
    const validation = validateLinkedInURL(normalizedURL)
    if (!validation.isValid) {
      toast.error(validation.error)
      return
    }

    setFormData(prev => ({
      ...prev,
      linkedin: {
        ...prev.linkedin,
        tagProfiles: [...prev.linkedin.tagProfiles, normalizedURL]
      }
    }))
    setNewLinkedInProfile('')
    setErrors({ ...errors, linkedinProfiles: '' })
  }

  const handleRemoveLinkedInProfile = (index) => {
    setFormData(prev => ({
      ...prev,
      linkedin: {
        ...prev.linkedin,
        tagProfiles: prev.linkedin.tagProfiles.filter((_, i) => i !== index)
      }
    }))
  }

  const handleAddFacebookProfile = () => {
    if (!newFacebookProfile.trim()) return

    if (formData.facebook.tagProfiles.length >= 5) {
      toast.error('Maximum 5 Facebook profile links allowed')
      return
    }

    // Normalize URL (add https:// if missing)
    const normalizedURL = normalizeURL(newFacebookProfile.trim())

    // Validate Facebook URL before adding
    const validation = validateFacebookURL(normalizedURL)
    if (!validation.isValid) {
      toast.error(validation.error)
      return
    }

    setFormData(prev => ({
      ...prev,
      facebook: {
        ...prev.facebook,
        tagProfiles: [...prev.facebook.tagProfiles, normalizedURL]
      }
    }))
    setNewFacebookProfile('')
    setErrors({ ...errors, facebookProfiles: '' })
  }

  const handleRemoveFacebookProfile = (index) => {
    setFormData(prev => ({
      ...prev,
      facebook: {
        ...prev.facebook,
        tagProfiles: prev.facebook.tagProfiles.filter((_, i) => i !== index)
      }
    }))
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      const isUpdate = existingData && existingData._id

      const response = isUpdate
        ? await companyAPI.updateBrandPromotion(formData)
        : await companyAPI.saveBrandPromotion(formData)

      if (response.data.success) {
        toast.success(isUpdate ? 'Brand promotion updated successfully!' : 'Brand promotion saved successfully!')
        onClose()
      }
    } catch (error) {
      console.error('Error saving brand promotion:', error)
      toast.error(error.response?.data?.message || 'Failed to save brand promotion')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader className="pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Building2 className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Brand Promotion</DialogTitle>
              <DialogDescription className="mt-1">
                Provide your social media profiles for promotional activities
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {fetchingData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            <span className="ml-2">Loading existing data...</span>
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[calc(90vh-200px)] px-1">
            <div className="space-y-6 py-4">
              {/* X (Twitter) Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Twitter className="h-5 w-5 text-gray-700" />
                  <h3 className="text-lg font-semibold">X (formerly Twitter)</h3>
                </div>

                <div className="space-y-4 pl-7">
                  <div className="space-y-2">
                    <Label htmlFor="twitterProfile">Organisation's X Profile Link</Label>
                    <Input
                      id="twitterProfile"
                      placeholder="https://twitter.com/yourcompany"
                      value={formData.twitter.organizationProfile}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          twitter: { ...prev.twitter, organizationProfile: e.target.value }
                        }))
                        // Clear error when user types
                        if (errors.twitter) {
                          setErrors({ ...errors, twitter: '' })
                        }
                      }}
                      onBlur={(e) => {
                        // Normalize URL (add https:// if missing) and validate if value is provided
                        if (e.target.value && e.target.value.trim()) {
                          const normalizedURL = normalizeURL(e.target.value)
                          setFormData(prev => ({
                            ...prev,
                            twitter: { ...prev.twitter, organizationProfile: normalizedURL }
                          }))
                          const validation = validateTwitterURL(normalizedURL)
                          if (!validation.isValid) {
                            setErrors({ ...errors, twitter: validation.error })
                          }
                        }
                      }}
                      className={errors.twitter ? 'border-red-500' : ''}
                    />
                    {errors.twitter && (
                      <p className="text-sm text-red-500">{errors.twitter}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Profile Links (0-5)</Label>
                    <p className="text-sm text-muted-foreground">
                      Add X profile URLs to tag (CXOs, Marketing, PR teams)
                    </p>

                    {/* Add new profile input */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="https://twitter.com/username"
                        value={newTwitterProfile}
                        onChange={(e) => setNewTwitterProfile(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddTwitterProfile()}
                        disabled={formData.twitter.tagProfiles.length >= 5}
                      />
                      <Button
                        onClick={handleAddTwitterProfile}
                        disabled={formData.twitter.tagProfiles.length >= 5}
                        variant="outline"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </Button>
                    </div>

                    {/* Display added profiles as line items */}
                    {formData.twitter.tagProfiles.length > 0 && (
                      <div className="space-y-2 mt-3">
                        {formData.twitter.tagProfiles.map((profile, index) => (
                          <div key={index} className="flex items-center justify-between p-2 border rounded-lg hover:bg-gray-50">
                            <span className="text-sm truncate flex-1">{profile}</span>
                            <button
                              onClick={() => handleRemoveTwitterProfile(index)}
                              className="ml-2 p-1 hover:bg-gray-200 rounded"
                            >
                              <X className="h-4 w-4 text-gray-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {errors.twitterProfiles && (
                      <p className="text-sm text-red-500">{errors.twitterProfiles}</p>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* LinkedIn Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Linkedin className="h-5 w-5 text-blue-700" />
                  <h3 className="text-lg font-semibold">LinkedIn</h3>
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-md font-medium">Required</span>
                </div>

                <div className="space-y-4 pl-7">
                  <div className="space-y-2">
                    <Label htmlFor="linkedinProfile">
                      Organisation's LinkedIn Profile Link *
                    </Label>
                    <Input
                      id="linkedinProfile"
                      placeholder="https://linkedin.com/company/yourcompany"
                      value={formData.linkedin.organizationProfile}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          linkedin: { ...prev.linkedin, organizationProfile: e.target.value }
                        }))
                        // Clear error when user types
                        if (errors.linkedin) {
                          setErrors({ ...errors, linkedin: '' })
                        }
                      }}
                      onBlur={(e) => {
                        // Normalize URL (add https:// if missing) and validate
                        const normalizedURL = normalizeURL(e.target.value)
                        if (normalizedURL) {
                          setFormData(prev => ({
                            ...prev,
                            linkedin: { ...prev.linkedin, organizationProfile: normalizedURL }
                          }))
                          const validation = validateLinkedInURL(normalizedURL)
                          if (!validation.isValid) {
                            setErrors({ ...errors, linkedin: validation.error })
                          }
                        }
                      }}
                      className={errors.linkedin ? 'border-red-500' : ''}
                    />
                    {errors.linkedin && (
                      <p className="text-sm text-red-500">{errors.linkedin}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Example: https://linkedin.com/company/yourcompany
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Profile Links (0-5)</Label>
                    <p className="text-sm text-muted-foreground">
                      Add LinkedIn profile URLs to tag (CXOs, Marketing, PR teams)
                    </p>

                    {/* Add new profile input */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="https://linkedin.com/in/username"
                        value={newLinkedInProfile}
                        onChange={(e) => setNewLinkedInProfile(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddLinkedInProfile()}
                        disabled={formData.linkedin.tagProfiles.length >= 5}
                      />
                      <Button
                        onClick={handleAddLinkedInProfile}
                        disabled={formData.linkedin.tagProfiles.length >= 5}
                        variant="outline"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </Button>
                    </div>

                    {/* Display added profiles as line items */}
                    {formData.linkedin.tagProfiles.length > 0 && (
                      <div className="space-y-2 mt-3">
                        {formData.linkedin.tagProfiles.map((profile, index) => (
                          <div key={index} className="flex items-center justify-between p-2 border rounded-lg hover:bg-gray-50">
                            <span className="text-sm truncate flex-1">{profile}</span>
                            <button
                              onClick={() => handleRemoveLinkedInProfile(index)}
                              className="ml-2 p-1 hover:bg-gray-200 rounded"
                            >
                              <X className="h-4 w-4 text-gray-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {errors.linkedinProfiles && (
                      <p className="text-sm text-red-500">{errors.linkedinProfiles}</p>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Facebook Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Facebook className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold">Facebook</h3>
                </div>

                <div className="space-y-4 pl-7">
                  <div className="space-y-2">
                    <Label htmlFor="facebookProfile">Organisation's Facebook Profile Link</Label>
                    <Input
                      id="facebookProfile"
                      placeholder="https://facebook.com/yourcompany"
                      value={formData.facebook.organizationProfile}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          facebook: { ...prev.facebook, organizationProfile: e.target.value }
                        }))
                        // Clear error when user types
                        if (errors.facebook) {
                          setErrors({ ...errors, facebook: '' })
                        }
                      }}
                      onBlur={(e) => {
                        // Normalize URL (add https:// if missing) and validate if value is provided
                        if (e.target.value && e.target.value.trim()) {
                          const normalizedURL = normalizeURL(e.target.value)
                          setFormData(prev => ({
                            ...prev,
                            facebook: { ...prev.facebook, organizationProfile: normalizedURL }
                          }))
                          const validation = validateFacebookURL(normalizedURL)
                          if (!validation.isValid) {
                            setErrors({ ...errors, facebook: validation.error })
                          }
                        }
                      }}
                      className={errors.facebook ? 'border-red-500' : ''}
                    />
                    {errors.facebook && (
                      <p className="text-sm text-red-500">{errors.facebook}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Profile Links (0-5)</Label>
                    <p className="text-sm text-muted-foreground">
                      Add Facebook profile URLs to tag (CXOs, Marketing, PR teams)
                    </p>

                    {/* Add new profile input */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="https://facebook.com/username"
                        value={newFacebookProfile}
                        onChange={(e) => setNewFacebookProfile(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddFacebookProfile()}
                        disabled={formData.facebook.tagProfiles.length >= 5}
                      />
                      <Button
                        onClick={handleAddFacebookProfile}
                        disabled={formData.facebook.tagProfiles.length >= 5}
                        variant="outline"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </Button>
                    </div>

                    {/* Display added profiles as line items */}
                    {formData.facebook.tagProfiles.length > 0 && (
                      <div className="space-y-2 mt-3">
                        {formData.facebook.tagProfiles.map((profile, index) => (
                          <div key={index} className="flex items-center justify-between p-2 border rounded-lg hover:bg-gray-50">
                            <span className="text-sm truncate flex-1">{profile}</span>
                            <button
                              onClick={() => handleRemoveFacebookProfile(index)}
                              className="ml-2 p-1 hover:bg-gray-200 rounded"
                            >
                              <X className="h-4 w-4 text-gray-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {errors.facebookProfiles && (
                      <p className="text-sm text-red-500">{errors.facebookProfiles}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* General Error */}
              {errors.general && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <span className="text-sm text-red-700">{errors.general}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || fetchingData || !formData.linkedin.organizationProfile}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              existingData ? 'Update' : 'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}